/** Vacation house checks: today's list for patrol, upcoming ones, office entry, the public page switch, and each check's log. */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, EmptyState, when } from './common.tsx';

const EMPTY = { residentName: '', phone: '', email: '', address: '', city: '', zip: '', startsOn: '', endsOn: '', contactName: '', contactPhone: '', details: '', emailUpdates: true };

function NewCheck({ onDone }: { onDone: (id: string) => void }) {
  const [f, setF] = useState(EMPTY);
  const a = useAction();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <Card title="New house check (taken by phone or at the desk)">
      <div className="grid-3">
        <label>
          Resident *
          <input value={f.residentName} onChange={set('residentName')} />
        </label>
        <label>
          Phone
          <input value={f.phone} onChange={set('phone')} inputMode="tel" />
        </label>
        <label>
          Email
          <input type="email" value={f.email} onChange={set('email')} />
        </label>
        <label>
          Address *
          <input value={f.address} onChange={set('address')} />
        </label>
        <label>
          City
          <input value={f.city} onChange={set('city')} />
        </label>
        <label>
          ZIP
          <input value={f.zip} onChange={set('zip')} />
        </label>
        <label>
          Leaving *
          <input type="date" value={f.startsOn} onChange={set('startsOn')} />
        </label>
        <label>
          Back *
          <input type="date" value={f.endsOn} onChange={set('endsOn')} />
        </label>
        <label>
          Local contact
          <input value={f.contactName} onChange={set('contactName')} placeholder="Name" />
        </label>
        <label>
          Contact’s phone
          <input value={f.contactPhone} onChange={set('contactPhone')} inputMode="tel" />
        </label>
      </div>
      <label>
        Notes for deputies
        <textarea rows={3} value={f.details} onChange={set('details')} placeholder="Cars that will be there, house sitter, alarm, dogs, lights on timers" />
      </label>
      {f.email && (
        <label className="check">
          <input type="checkbox" checked={f.emailUpdates} onChange={(e) => setF({ ...f, emailUpdates: e.target.checked })} /> Email the resident after each check
        </label>
      )}
      <ErrorBox error={a.error} />
      <div>
        <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => onDone((await api.post<any>('/api/house-checks', f)).id))}>
          Save house check
        </button>
      </div>
    </Card>
  );
}

function PublicSwitch() {
  const r = useLoad(() => api.get<any>('/api/house-checks/settings'));
  const a = useAction();
  if (!r.data) return null;
  const url = `${location.origin}${r.data.link}`;
  return (
    <Card title="Public request page">
      <p className="small">
        {r.data.public ? (
          <>
            On. Residents can ask at <a href={r.data.link} target="_blank" rel="noreferrer">{url}</a> — put it on the website and social media.
          </>
        ) : (
          'Off. Turn it on to let residents ask for house checks online.'
        )}
      </p>
      <ErrorBox error={a.error} />
      {r.data.canChange && (
        <div>
          <button className="btn" disabled={a.busy} onClick={() => a.run(async () => (await api.post('/api/house-checks/settings', { public: !r.data.public }), r.reload()))}>
            {r.data.public ? 'Turn off the public page' : 'Turn on the public page'}
          </button>
        </div>
      )}
    </Card>
  );
}

export function HouseChecksPage() {
  const { navigate } = useRouter();
  const [status, setStatus] = useState('today');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const r = useLoad(() => api.get<any>(`/api/house-checks?status=${status}&q=${encodeURIComponent(q)}`), [status, q]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Patrol"
        title="House checks"
        sub="Homes to check while the residents are away. Log each check; the resident is emailed if they asked."
        actions={
          r.data?.canEnter && (
            <div className="row gap-sm">
              <button className="btn" onClick={() => setShowSettings(!showSettings)}>
                Public page
              </button>
              <button className="btn primary" onClick={() => setAdding(!adding)}>
                New house check
              </button>
            </div>
          )
        }
      />
      {showSettings && <PublicSwitch />}
      {adding && <NewCheck onDone={(id) => navigate(at(`/house-checks/${id}`))} />}
      <div className="row gap-sm wrap">
        <div className="seg-ctl" role="tablist" aria-label="Which house checks">
          {(
            [
              ['today', 'Today'],
              ['upcoming', 'Coming up'],
              ['all', 'All'],
            ] as const
          ).map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={status === k} className={status === k ? 'on' : ''} onClick={() => setStatus(k)}>
              {l}
            </button>
          ))}
        </div>
        <input type="search" placeholder="Address, name, number…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search house checks" style={{ maxWidth: 260 }} />
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.checks.length ? (
        <EmptyState icon="check" title={status === 'today' ? 'No house checks today' : 'None'} />
      ) : (
        <div className="card table-wrap">
          <table className="table clickable">
            <thead>
              <tr>
                <th>Address</th>
                <th>Resident</th>
                <th>Dates</th>
                <th>Last check</th>
              </tr>
            </thead>
            <tbody>
              {r.data.checks.map((c: any) => (
                <tr key={c.id} onClick={() => navigate(at(`/house-checks/${c.id}`))}>
                  <td>
                    <strong>{c.address}</strong>
                    {c.city && <span className="muted">, {c.city}</span>}
                    <div className="small muted mono">{c.number}</div>
                  </td>
                  <td>{c.resident_name}</td>
                  <td className="small">
                    {day(String(c.starts_on).slice(0, 10))} – {day(String(c.ends_on).slice(0, 10))}
                    {c.status !== 'active' && (
                      <div>
                        <Badge>{c.status === 'done' ? 'Done' : 'Canceled'}</Badge>
                      </div>
                    )}
                  </td>
                  <td className="small">
                    {status === 'today' && (c.checkedToday ? <Badge tone="ok">Checked today</Badge> : <Badge tone="warn">Not yet today</Badge>)}{' '}
                    {c.last_visit ? `${when(c.last_visit)}${c.last_result === 'issue' ? ' · problem found' : ''}` : status !== 'today' ? '—' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function HouseCheckDetail({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/house-checks/${id}`), [id]);
  const a = useAction();
  const [note, setNote] = useState('');
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { check: c, visits } = r.data;
  const log = (result: 'secure' | 'issue') => a.run(async () => (await api.post(`/api/house-checks/${id}/visit`, { result, note: note || undefined }), setNote(''), r.reload()));
  const setStatus = (status: string, ask: string) => {
    const n = window.prompt(ask);
    if (n === null) return;
    a.run(async () => (await api.post(`/api/house-checks/${id}`, { status, note: n || undefined }), r.reload()));
  };
  const mapUrl = `https://maps.google.com/?q=${encodeURIComponent([c.address, c.city, c.zip].filter(Boolean).join(', '))}`;
  return (
    <div className="ops stack">
      <PageHeader
        crumb="House checks"
        title={c.address}
        sub={`${c.number} · ${day(String(c.starts_on).slice(0, 10))} – ${day(String(c.ends_on).slice(0, 10))}`}
        actions={
          r.data.canEdit &&
          c.status === 'active' && (
            <div className="row gap-sm">
              <button className="btn" disabled={a.busy} onClick={() => setStatus('canceled', 'Why is it canceled? (e.g. came home early)')}>
                Cancel
              </button>
            </div>
          )
        }
      />
      {c.status !== 'active' && <Notice tone="info">{c.status === 'done' ? 'This house check is over.' : `Canceled${c.status_note ? `: ${c.status_note}` : ''}.`}</Notice>}
      <ErrorBox error={a.error} />
      {r.data.canLog && (
        <Card title="Log a check">
          <label>
            Note
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What you checked, or what you found" />
          </label>
          <div className="row gap-sm wrap">
            <button className="btn primary" disabled={a.busy} onClick={() => log('secure')}>
              All secure
            </button>
            <button className="btn danger" disabled={a.busy} onClick={() => log('issue')}>
              Found a problem
            </button>
          </div>
          {c.email && c.email_updates && <p className="small muted">The resident is emailed when you log a check.</p>}
        </Card>
      )}
      <Card title="The house">
        <dl className="kv">
          <dt>Address</dt>
          <dd>
            {[c.address, c.city, c.zip].filter(Boolean).join(', ')}{' '}
            <a className="small" href={mapUrl} target="_blank" rel="noreferrer">
              Map
            </a>
          </dd>
          <dt>Resident</dt>
          <dd>
            {c.resident_name}
            {c.phone && (
              <>
                {' · '}
                <a href={`tel:${c.phone}`}>{c.phone}</a>
              </>
            )}
            {c.email && ` · ${c.email}`}
          </dd>
          {c.contact_name && (
            <>
              <dt>Local contact</dt>
              <dd>
                {c.contact_name}
                {c.contact_phone && (
                  <>
                    {' · '}
                    <a href={`tel:${c.contact_phone}`}>{c.contact_phone}</a>
                  </>
                )}
              </dd>
            </>
          )}
          {c.details && (
            <>
              <dt>Notes</dt>
              <dd style={{ whiteSpace: 'pre-wrap' }}>{c.details}</dd>
            </>
          )}
          <dt>Taken</dt>
          <dd className="small">
            {c.source === 'public' ? 'Online request' : `By ${c.created_by_name ?? 'the office'}`} · {when(c.created_at)}
          </dd>
        </dl>
      </Card>
      <Card title={`Checks (${visits.length})`}>
        {!visits.length ? (
          <p className="muted">No checks logged yet.</p>
        ) : (
          <ul className="plain">
            {visits.map((v: any) => (
              <li key={v.id}>
                {v.result === 'secure' ? <Badge tone="ok">Secure</Badge> : <Badge tone="bad">Problem</Badge>} <strong>{when(v.at)}</strong> · {v.by_name}
                {v.call_number ? ` (${v.call_number})` : ''}
                {v.note && <div className="small">{v.note}</div>}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
